import React, {Fragment, useEffect, useState} from "react";
import {Input, InputGroup, Loader, toaster} from "rsuite";
import {GetDevice_GetDeviceResponse} from "../../api";
import {SharedApi} from "../../services/Shared.api";
import {DeviceInfoComponentProps} from "./DeviceInfoComponent.model";
import NotificationFactory from "../../shared/Notifications";
import {TfiSaveIcon} from "../../models/Icons.model";
import DeviceParameterSyncButton from "../shared/DeviceParameterSyncButton/DeviceParameterSyncButton";
import {ExternalDevicesUtils} from "../menu/ExternalDevices/ExternalDevices.utils";
import FirmwareUpdateIndicator from '../shared/FirmwareUpdateIndicator/FirmwareUpdateIndicator';

const DeviceInfoComponent = ({deviceId, isAdmin, deviceData, deviceType}: DeviceInfoComponentProps) => {

  const [loading, setLoading] = useState(!deviceData);
  const [device, setDevice] = useState<GetDevice_GetDeviceResponse | null>(deviceData ?? null);

  useEffect(() => {
    if (deviceData) {
      setDevice(deviceData);
      setLoading(false);
      return;
    }

    if (deviceId !== null) {
      getData().then()
    }
  }, [deviceId, deviceData]);

  const getData = async () => {
    try {
      setLoading(true);
      const deviceData = await SharedApi.getDeviceInfo(String(deviceId));
      setDevice(deviceData)

    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false);
    }
  }

  const onChangePlace = (value: string) => {
    setDevice(prev => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        place: value
      };
    });
  }

  const onSavePlace = async () => {
    setLoading(true);
    try {
      await SharedApi.updatePlace(String(deviceId), device?.place || "");
      toaster.push(
        NotificationFactory.success(),
        {placement: "topEnd"}
      );
      await getData().then()
    } catch (e) {
      console.error('Ошибка при сохранении места:', e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  }

  const onSaveArticle = async () => {
    setLoading(true);
    try {
      await SharedApi.updateArticle(String(deviceId), device?.article || "");
      toaster.push(
        NotificationFactory.success(),
        {placement: "topEnd"}
      );
      await getData().then();
    } catch (e) {
      console.error('Ошибка при сохранении артикула:', e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  }

  const onSaveModel = async () => {
    setLoading(true);
    try {
      await SharedApi.updateModel(String(deviceId), device?.model || "");
      toaster.push(
        NotificationFactory.success(),
        {placement: "topEnd"}
      );
      await getData().then();
    } catch (e) {
      console.error('Ошибка при сохранении модели:', e);
      toaster.push(
        NotificationFactory.error(),
        {placement: "topEnd"}
      );
    } finally {
      setLoading(false);
    }
  }

  const onChangeArticle = (value: string) => {
    setDevice(prev => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        article: value
      };
    });
  }

  const onChangeModel = (value: string) => {
    setDevice(prev => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        model: value
      };
    });
  }

  const renderDeviceInfo = (device: GetDevice_GetDeviceResponse | null) => {
    if (!device)
      return null;

    return (
      <>
        <div className="row p-1 mt-3">
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Артикул</InputGroup.Addon>
              <Input
                size={"md"}
                value={device.article || ''}
                onChange={onChangeArticle}
              />
              {isAdmin && (
                <InputGroup.Addon>
                  <TfiSaveIcon
                    style={{cursor: 'pointer'}}
                    onClick={onSaveArticle}
                    className={loading ? "loading" : ""}
                  />
                </InputGroup.Addon>
              )}
            </InputGroup>
          </div>
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Модель</InputGroup.Addon>
              <Input
                size={"md"}
                value={device.model || ''}
                onChange={onChangeModel}
              />
              {isAdmin && (
                <InputGroup.Addon>
                  <TfiSaveIcon
                    style={{cursor: 'pointer'}}
                    onClick={onSaveModel}
                    className={loading ? "loading" : ""}
                  />
                </InputGroup.Addon>
              )}
            </InputGroup>
          </div>
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Место установки</InputGroup.Addon>
              <Input
                size={"md"}
                value={device?.place || ''}
                onChange={onChangePlace}
              />
              {isAdmin && (
                <DeviceParameterSyncButton
                  deviceId={String(deviceId)}
                  parameter="Place"
                  onSynced={getData}
                />
              )}
              <InputGroup.Addon>
                <TfiSaveIcon
                  style={{cursor: 'pointer'}}
                  onClick={onSavePlace}
                  className={loading ? "loading" : ""}
                />
              </InputGroup.Addon>
            </InputGroup>
          </div>
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Версия прошивки:</InputGroup.Addon>
              <Input
                size={"md"}
                value={device.firmwareVersion || ''}
              />
              <FirmwareUpdateIndicator visible={device.hasNewerFirmwareVersion}/>
              {isAdmin && (
                <DeviceParameterSyncButton
                  deviceId={String(deviceId)}
                  parameter="FirmwareVersion"
                  onSynced={getData}
                />
              )}
            </InputGroup>
          </div>
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Дата производства:</InputGroup.Addon>
              <Input
                size={"md"}
                value={device.fabricDate || ''}
              />
            </InputGroup>
          </div>
          <div className="col-md-12 mb-1">
            <InputGroup>
              <InputGroup.Addon>Серийный номер:</InputGroup.Addon>
              <Input
                size={"md"}
                value={ExternalDevicesUtils.formatDeviceSerialNumber(device.serialNumber, deviceType, device.model)}
              />
            </InputGroup>
          </div>
        </div>
      </>
    )
  }

  return (
    <Fragment>
      {
        loading ?
          <Loader size={"md"} content="Загрузка информации об устройстве"/>
          :
          renderDeviceInfo(device)
      }
    </Fragment>
  );
}

export default DeviceInfoComponent;
